import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { caseKeys } from '@/hooks/use-case-queries'
import type { CaseData } from '@/pages/cases/CaseContext'
import { api } from '@/services/api'

/** Loads a case's detail and self-heals stale cached counts.
 *
 *  The detail endpoint serves entity/relationship counts from cached SQL
 *  columns; cases extracted before that cache-write was wired up report 0 even
 *  when Neo4j is populated. When counts look stale (entities==0 but
 *  documents>0) we trigger one refresh per case load and refetch. */
export function useCaseDetail(caseId: string) {
  const queryClient = useQueryClient()
  const caseQuery = useQuery<CaseData>({
    queryKey: caseKeys.detail(caseId),
    queryFn: () => api.cases.get(caseId) as unknown as Promise<CaseData>,
  })

  const refreshAttemptedRef = useRef<string | null>(null)
  useEffect(() => {
    const data = caseQuery.data
    if (!data) return
    if (refreshAttemptedRef.current === caseId) return
    if (data.entity_count > 0) return
    if ((data.document_count ?? 0) <= 0) return
    refreshAttemptedRef.current = caseId
    void api.cases
      .refreshCounts(caseId)
      .then(() => queryClient.invalidateQueries({ queryKey: caseKeys.detail(caseId) }))
      .catch(() => {
        refreshAttemptedRef.current = null
      })
  }, [caseQuery.data, caseId, queryClient])

  return caseQuery
}
